
import { Autocomplete, Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DescriptionIcon from "@mui/icons-material/Description";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import supplierMasterService from "../../../services/masters/supplierMaster.Service";
import ReusableSnackbar from "../../reusableComponent/ReusableSnackbar";

// ── Styles (PlantAdd pattern) ─────────────────────────────────────────────────

const pageSx = {
  background: "#F4F7FB",
  minHeight: "100vh",
  padding: "20px 24px 40px",
  fontFamily: "'Segoe UI', Arial, sans-serif",
};

const breadcrumbSx = {
  fontSize: "12px",
  color: "#718096",
  mb: 2,
  display: "flex",
  alignItems: "center",
  gap: "6px",
};

const pageHeaderSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  mb: 2.5,
};

const pageTitleSx = {
  fontSize: "16px",
  fontWeight: 700,
  color: "#000",
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const backBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "13px",
  fontWeight: 600,
  color: "#2B6CB0",
  padding: "7px 14px",
  border: "1.5px solid #2B6CB0",
  borderRadius: "7px",
  background: "#fff",
  cursor: "pointer",
  textTransform: "none",
  "&:hover": { background: "#E6F1FB" },
};

const formCardSx = {
  background: "#fff",
  
  border: "1px solid #E2E8F0",
  overflow: "hidden",
};

const sectionHeaderSx = {
  background: "#1C3A5E",
  padding: "12px 22px",
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const sectionTitleSx = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#fff",
  letterSpacing: "0.3px",
};

const formBodySx = { padding: "22px 24px" };

const fieldLabelSx = {
  fontSize: "12px",
  fontWeight: 600,
  color: "#2063b6",
  mb: "4px",
  display: "block",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "13px",
    background: "#fafafa",
    height: "36px",
    "& fieldset": { borderColor: "#E2E8F0", borderWidth: "1.5px" },
    "&:hover fieldset": { borderColor: "#CBD5E0" },
    "&.Mui-focused fieldset": { borderColor: "#2B6CB0", borderWidth: "2px" },
    "&.Mui-focused": { background: "#fff" },
    "&.Mui-disabled": { background: "#F0F0F0" },
  },
  "& input": {
    color: "#1A202C",
    fontSize: "13px",
    "&::placeholder": { color: "#A0AEC0", opacity: 1 },
  },
};

const readonlyInputSx = {
  ...inputSx,
  "& .MuiOutlinedInput-root": {
    ...inputSx["& .MuiOutlinedInput-root"],
    background: "#F0F0F0",
  },
};

const formFooterSx = {
  padding: "16px 24px",
  background: "#F8FAFC",
  borderTop: "1px solid #E2E8F0",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "10px",
};

const saveBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "7px",
  padding: "0 22px",
  height: "38px",
  background: "#4CAF50",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  fontSize: "14px",
  fontWeight: 600,
  textTransform: "none",
  "&:hover": { background: "#388E3C" },
  "&.Mui-disabled": { background: "#94a3b8", color: "#fff" },
};

const cancelBtnSx = {
  display: "flex",
  alignItems: "center",
  gap: "7px",
  padding: "0 22px",
  height: "38px",
  background: "#E53E3E",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  fontSize: "14px",
  fontWeight: 600,
  textTransform: "none",
  "&:hover": { background: "#C53030" },
};

// ─────────────────────────────────────────────────────────────────────────────

const STATE_LIST = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Puducherry"
];

const SupplierAdd = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const urlLocation = useLocation();
  const editData = urlLocation.state;
  const { handleSubmit } = useForm();

  const login_details = sessionStorage.getItem('loginUser');
  const userD = JSON.parse(login_details);
  const userInfo = userD.result.userInfo;

  const [supplier, setSupplier] = useState([]);
  const [successMessage, setSuccessMessage] = useState(null);

  const [data, setData] = useState({
    supplier_name: editData?.supplier_name || "",
    contact_person: editData?.contact_person || "",
    mobile_no: editData?.mobile_no || "",
    email_id: editData?.email_id || "",
    gstn: editData?.gstn || "",
    panNo: editData?.panNo || "",
    state: editData?.state || "",
    credit_limit: editData?.credit_limit || 0,
    opening_balance: editData?.opening_balance || 0,
    credit_period: editData?.credit_period || 0,
    vendor_code: editData?.vendor_code || "",
    supplier_address: editData?.supplier_address || "",
    city: editData?.city || "",
    pin_code: editData?.pin_code || "",
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const supplierData = await supplierMasterService.getSupplierList({ page: 0, size: 999, searchKey: "" });
        setSupplier(supplierData);
      } catch (error) {
        console.error("Data fetching failed", error);
      }
    }
    fetchData();
  }, []);

  const handleChange = ({ target }) => {
    setData(prev => ({ ...prev, [target.name]: target.value }));
  };

  const onSubmit = async () => {
    const supplierData = {
      ...data,
      modified_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      modified_by: editData ? userInfo.user_id : null,
      created_at: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      created_by: editData ? editData.created_by : userInfo.user_id,
      changedUpdatedValue: 'edit',
      supplier_id: editData ? editData.supplier_id : '',
    };
    try {
      if (editData) {
        await supplierMasterService.editDeleteSupplier(supplierData);
      } else {
        await supplierMasterService.addSupplier(supplierData);
      }
      setSuccessMessage(editData ? "Updated successfully" : "Added successfully");
      setTimeout(() => navigate(-1), 1000);
    } catch (error) {
      setSuccessMessage("Error occurred");
    }
  };

  return (
    <Box sx={pageSx}>

      {/* Snackbar */}
      <ReusableSnackbar
        open={!!successMessage}
        onClose={(_, reason) => { if (reason !== 'clickaway') setSuccessMessage(""); }}
        message={successMessage}
        autoHideDuration={2000}
        vertical="top"
        horizontal="right"
      />

      {/* Breadcrumb */}
      <Box sx={breadcrumbSx}>
        Dashboard / Masters /{" "}
        <span style={{ color: "#2B6CB0", fontWeight: 600 }}>Supplier</span>
      </Box>

      {/* Page Header */}
      <Box sx={pageHeaderSx}>
        <Box sx={pageTitleSx}>
          <DescriptionIcon sx={{ fontSize: 20, color: "#1C3A5E" }} />
          {editData ? "UPDATE" : "ADD"} SUPPLIER MASTER
        </Box>
        <Button onClick={() => navigate(-1)} sx={backBtnSx}>
          <ArrowBackIcon sx={{ fontSize: 16 }} /> Back
        </Button>
      </Box>

      {/* Main Card */}
      <Box sx={formCardSx}>

        {/* Navy Header */}
        <Box sx={sectionHeaderSx}>
          <DescriptionIcon sx={{ fontSize: 18, color: "#fff" }} />
          <Typography sx={sectionTitleSx}>Supplier Master</Typography>

          {/* Last Entry No — only in Add mode */}
          {!editData && supplier?.length > 0 && (
            <Typography sx={{ ml: "auto", fontSize: "12px", color: "#cbd5e0" }}>
              Last Entry No : {supplier[0].lastSupplierEntryNo}
            </Typography>
          )}
          {!editData && supplier?.length === 0 && (
            <Typography sx={{ ml: "auto", fontSize: "12px", color: "#cbd5e0" }}>
              Last Entry No : 0
            </Typography>
          )}
        </Box>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={formBodySx}>
            <Grid container spacing={3}>

              {/* Supplier Name */}
              <Grid item xs={12} md={6}>
                <Typography sx={fieldLabelSx}>
                  Supplier Name <span style={{ color: "#E53E3E" }}>*</span>
                </Typography>
                <TextField
                  size="small" fullWidth required
                  name="supplier_name" value={data.supplier_name}
                  onChange={handleChange}
                  placeholder="Enter supplier name"
                  sx={inputSx}
                />
              </Grid>

              {/* Address */}
              <Grid item xs={12} md={6}>
                <Typography sx={fieldLabelSx}>
                  Address <span style={{ color: "#E53E3E" }}>*</span>
                </Typography>
                <TextField
                  size="small" fullWidth
                  name="supplier_address" value={data.supplier_address}
                  onChange={handleChange}
                  placeholder="Enter supplier address"
                  sx={inputSx}
                />
              </Grid>

              {/* City */}
              <Grid item xs={12} md={6}>
                <Typography sx={fieldLabelSx}>
                  City <span style={{ color: "#E53E3E" }}>*</span>
                </Typography>
                <TextField
                  size="small" fullWidth
                  name="city" value={data.city}
                  onChange={handleChange}
                  placeholder="Enter city"
                  sx={inputSx}
                />
              </Grid>

              {/* Pin Code */}
              <Grid item xs={12} md={6}>
                <Typography sx={fieldLabelSx}>
                  Pin Code <span style={{ color: "#E53E3E" }}>*</span>
                </Typography>
                <TextField
                  size="small" fullWidth
                  name="pin_code" value={data.pin_code}
                  onChange={handleChange}
                  placeholder="Enter pin code"
                  sx={inputSx}
                />
              </Grid>

              {/* State */}
              <Grid item xs={12} md={6}>
                <Typography sx={fieldLabelSx}>
                  State <span style={{ color: "#E53E3E" }}>*</span>
                </Typography>
                <Autocomplete
                  options={STATE_LIST}
                  getOptionLabel={(option) => typeof option === 'string' ? option : ''}
                  value={data.state || null}
                  onChange={(_, newValue) => handleChange({ target: { name: 'state', value: newValue || '' } })}
                  renderOption={(props, option) => (
                    <MenuItem {...props} sx={{ fontSize: "13px" }}>{option}</MenuItem>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small" fullWidth
                      placeholder="Select state"
                      sx={inputSx}
                    />
                  )}
                />
              </Grid>

              {/* Contact Person */}
              <Grid item xs={12} md={6}>
                <Typography sx={fieldLabelSx}>
                  Contact Person <span style={{ color: "#E53E3E" }}>*</span>
                </Typography>
                <TextField
                  size="small" fullWidth required
                  name="contact_person" value={data.contact_person}
                  onChange={handleChange}
                  placeholder="Enter contact person"
                  sx={inputSx}
                />
              </Grid>

              {/* Mobile No */}
              <Grid item xs={12} md={6}>
                <Typography sx={fieldLabelSx}>
                  Mobile No <span style={{ color: "#E53E3E" }}>*</span>
                </Typography>
                <TextField
                  size="small" fullWidth required
                  name="mobile_no" value={data.mobile_no}
                  onChange={handleChange}
                  placeholder="Enter mobile number"
                  sx={inputSx}
                />
              </Grid>

              {/* Email Id */}
              <Grid item xs={12} md={6}>
                <Typography sx={fieldLabelSx}>
                  Email Id <span style={{ color: "#E53E3E" }}>*</span>
                </Typography>
                <TextField
                  size="small" fullWidth
                  name="email_id" value={data.email_id}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  sx={inputSx}
                />
              </Grid>

              {/* GSTIN */}
              <Grid item xs={12} md={6}>
                <Typography sx={fieldLabelSx}>
                  GSTIN <span style={{ color: "#E53E3E" }}>*</span>
                </Typography>
                <TextField
                  size="small" fullWidth required
                  name="gstn" value={data.gstn}
                  onChange={handleChange}
                  placeholder="Enter GSTIN"
                  sx={inputSx}
                />
              </Grid>

              {/* Pan No */}
              <Grid item xs={12} md={6}>
                <Typography sx={fieldLabelSx}>
                  Pan No <span style={{ color: "#E53E3E" }}>*</span>
                </Typography>
                <TextField
                  size="small" fullWidth required
                  name="panNo" value={data.panNo}
                  onChange={handleChange}
                  placeholder="Enter PAN number"
                  sx={inputSx}
                />
              </Grid>

              {/* Vendor Code — readonly */}
              <Grid item xs={12} md={6}>
                <Typography sx={fieldLabelSx}>Vendor Code</Typography>
                <TextField
                  size="small" fullWidth
                  value={data.vendor_code}
                  placeholder="Auto-generated"
                  InputProps={{ readOnly: true }}
                  sx={readonlyInputSx}
                />
              </Grid>

            </Grid>
          </Box>

          {/* Footer Buttons */}
          <Box sx={formFooterSx}>
            <Button sx={cancelBtnSx} onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button type="submit" sx={saveBtnSx}>
              <SaveIcon sx={{ fontSize: 17 }} />
              {editData ? "Update" : "Save"}
            </Button>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default SupplierAdd;
